import { API, graphqlOperation } from "aws-amplify";

import { createCarrierUser } from "../../../graphql/mutations";
import {
  findExistingUserByEmail,
  performCreateUserReloadCarrier,
  performUpdateUserReloadCarrier,
} from "../../../graphql/carrier/carrierCRUD";
import {
  getCarrierUserAcl,
  hasCarrierLogin,
  normalizeCarrierUserType,
} from "../../../utils/carrierUserUtils";
import { UserStatus, UserType } from "../../../utils/constants";
import { emailCarrierInviteHTML } from "./users/emailCarrierInviteHTML";
import { emailInviteHTML } from "./users/emailInviteHTML";

/**
 * Invite a carrier company's own email address as its primary dispatch user.
 *
 * Every path that creates a carrier record ends here: the address on the
 * carrier record becomes a dispatch user — and, because it is the company's own
 * address, the primary account colleagues cannot delete (isCarrierAdmin reads
 * that from the address rather than from a type of its own). Nobody typed a
 * person's name on any of these paths, so the row carries the business name;
 * the dispatcher fixes it on their own profile.
 *
 * The address may already belong to somebody. A yard staff member or customer
 * contact cannot also be a carrier login, so those addresses get no invite —
 * the carrier record itself still stands. An existing carrier user is invited
 * into this carrier entry the same way a colleague invite would, keeping one
 * account per address.
 *
 * The company's other dispatch users are given this yard too, so nobody has to
 * re-grant a colleague list one profile at a time. That is not done here: a
 * manager holds only their own yard's Cognito group, and every junction query
 * filters rows by the caller's groups, so from this session another yard's
 * memberships do not exist to read or copy. The junction stream does it instead
 * (handleCarrierUserChange), off the row written below.
 *
 * @param {object} carrier - the saved carrier record (id, businessName, email)
 * @returns {Promise<{sent: boolean, reason?: string}>} reason when not sent:
 *   "no-email" | "email-in-use" | "already-member" | "promoted" |
 *   "driver-email" | "blocked"
 */
export const sendPrimaryDispatchInvite = async (carrier) => {
  const email = (carrier?.email || "").trim().toLowerCase();
  if (!email) return { sent: false, reason: "no-email" };

  const { carrierUser, customer, yardUser } = await findExistingUserByEmail(
    email,
  );
  if (yardUser || customer) return { sent: false, reason: "email-in-use" };

  // No blockAccess here: spread over an existing junction it would clear a
  // yard's access block as a side effect of sending an invite, handing a
  // blocked person the portal back as the primary account nobody can delete.
  // The create paths below set it explicitly instead, where false is the
  // correct value for a brand-new row rather than a silent revocation.
  const primaryYardCarrier = {
    type: UserType.CARRIER_DISPATCH,
    acl: getCarrierUserAcl(UserType.CARRIER_DISPATCH),
  };

  let emailContent;
  if (carrierUser) {
    const existing = (carrierUser.reloadYardCarriers?.items ?? []).find(
      (junction) =>
        junction?.reloadYardId === window.appUser?.reloadYardId &&
        junction?.carrierId === carrier.id,
    );
    if (
      existing &&
      normalizeCarrierUserType(existing.type) === UserType.CARRIER_DRIVER
    ) {
      // The company address belongs to one of its drivers (an active row) or
      // to a driver whose membership was retired (an inactive one). Either
      // way, promoting/reviving as dispatch would silently hand that person
      // the dispatch portal and user management, so leave the row alone and
      // let the manager sort the address out.
      return { sent: false, reason: "driver-email" };
    }
    if (existing?.blockAccess === true) {
      // This yard has revoked the person's access without deleting the row.
      // Promoting or reviving them would lift that block, so the yard has to
      // unblock deliberately first — becoming the undeletable primary account
      // is not something an invite should do to a blocked account by accident.
      return { sent: false, reason: "blocked" };
    }
    if (existing?.active === "true") {
      if (
        normalizeCarrierUserType(existing.type) === UserType.CARRIER_DISPATCH
      ) {
        // A working dispatch login already sits on the company address, which is
        // all being primary is now — nothing to write, and nothing to send.
        return { sent: false, reason: "already-member" };
      }
      // The row holds a type this build cannot route to a portal. The
      // invariant this flow exists for — every carrier has one working,
      // protected primary account — still applies, so repair the membership in
      // place. No email: the person already has a login here.
      await performUpdateUserReloadCarrier({
        ...existing,
        businessName: carrier.businessName,
        ...primaryYardCarrier,
      });
      return { sent: false, reason: "promoted" };
    }
    if (existing) {
      // A retired membership is revived rather than duplicated; deleted is
      // cleared alongside active, as everywhere a row comes back.
      await performUpdateUserReloadCarrier({
        ...existing,
        active: "true",
        deleted: false,
        businessName: carrier.businessName,
        ...primaryYardCarrier,
      });
    } else {
      await performCreateUserReloadCarrier(carrierUser, carrier, {
        ...primaryYardCarrier,
        blockAccess: false,
      });
    }
    // Holding a CarrierUser record is not the same as being able to sign in.
    // Deleting somebody's last membership deletes their Cognito account and
    // leaves the record standing, so the invitation is chosen by whether there
    // is a login to return to — not by whether a row came back.
    const invite = hasCarrierLogin(carrierUser)
      ? emailCarrierInviteHTML
      : emailInviteHTML;
    emailContent = invite(
      carrier.businessName,
      email,
      window.appUser?.firstName,
      window.appUser?.reloadYard?.name,
    );
  } else {
    const response = await API.graphql(
      graphqlOperation(createCarrierUser, {
        input: {
          status: UserStatus.PENDING_REG,
          email,
          userType: "dispatch",
          firstName: "Admin",
          lastName: carrier.businessName,
        },
      }),
    );
    await performCreateUserReloadCarrier(response.data.createCarrierUser, carrier, {
      ...primaryYardCarrier,
      blockAccess: false,
    });
    emailContent = emailInviteHTML(
      carrier.businessName,
      email,
      window.appUser?.firstName,
      window.appUser?.reloadYard?.name,
    );
  }

  const response = await API.post("transREST", "/sendEmail", {
    body: {
      to: email,
      bcc: ["support@transloads.co"],
      subject: "Your transloads.co Invite Is Waiting",
      template: "emailTemplate.html",
      htmlContent: emailContent,
    },
  });
  if (response.error) throw response.error;

  return { sent: true };
};

/** One notification per outcome, shared by every caller. */
export const notifyPrimaryInviteResult = (notify, carrier, result) => {
  const email = carrier?.email;
  if (result.sent) {
    notify(`Invite sent to ${email}.`);
  } else if (result.reason === "email-in-use") {
    notify(
      `${email} already belongs to a yard or customer user — no carrier invite sent.`,
      "warning",
    );
  } else if (result.reason === "already-member") {
    notify(`${email} is already this carrier's primary dispatch user.`, "info");
  } else if (result.reason === "promoted") {
    notify(
      `${email} already had a membership here — set up as this carrier's primary dispatch user.`,
      "info",
    );
  } else if (result.reason === "driver-email") {
    notify(
      `${email} belongs to one of this carrier's drivers — no primary dispatch invite sent.`,
      "warning",
    );
  } else if (result.reason === "blocked") {
    notify(
      `${email} is blocked at this yard — unblock the user first, then send the invite.`,
      "warning",
    );
  }
  // "no-email" stays silent: kiosk-created carriers arrive without an address,
  // and the Send Invite button on the carrier is the follow-up path.
};
